import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useIntl, FormattedDate } from "react-intl";
import { MdCancel, MdCheckCircle, MdDownload } from "react-icons/md";
import {
  operationGetResults,
  generateResultPdfOperacao,
  exportOperacaoJson,
  operationGetCarLayers,
  getResults,
  exportGlebaPropostaKml,
  operationGetGlebas,
  exportGlebaConsultaKml,
  generateResultPdf,
  exportSicorCar,
  exportCoordinatesSicor,
  getResultsMapLayers
} from "~/api";
import CriteriaList from "./components/CriteriaList";
import ExportButton from "./components/ExportButton";
import DocumentCriteria from "./components/DocumentCriteria";
import AreaCriteria from "./components/AreaCriteria";
import Skeleton from "../../../../../components/Skeleton";
import CarMap from "../../CarMap";
import { CSVLink } from "react-csv";

import "./styles.scss";
import { useOperation } from "~/hooks/useOperation";
import AreaMap from "~/components/AreaMap";
import OverlapPlots from "~/components/OverlapPlots";

const SHOW_LAYERS = 1;

const OperationResults = ({ stepLabel, setError }) => {
  const { operation, operationStartFrom } = useOperation();
  const { formatMessage, formatNumber } = useIntl();
  const [resultLoading, setResultLoading] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportList, setReportList] = useState([]);
  const [operationCars, setOperationCars] = useState([]);
  const [operationGlebas, setOperationGlebas] = useState([]);
  const [loadingItems, setLoadingItems] = useState([]);
  const [openCriteria, setOpenCriteria] = useState([]);
  const [results, setResults] = useState(null);
  const [resultType, setResultType] = useState("geral");
  const [csvExport, setCsvExport] = useState([]);
  const [csvReport, setCsvReport] = useState([]);
  const [selectedCriteria, setSelectedCriteria] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("all");
  const [isLoading, setIsLoading] = useState(false);
  const [glebaLayers, setGlebaLayers] = useState(null);
  const csvref = useRef();

  const removeLoadingItem = useCallback(item => {
    setLoadingItems(oldLoadingItems =>
      oldLoadingItems.filter(oldItem => oldItem !== item)
    );
  }, []);

  const addLoadingItem = useCallback(
    item => {
      if (loadingItems.includes(item)) return;
      setLoadingItems(oldLoadingItems => [...oldLoadingItems, item]);
    },
    [loadingItems]
  );

  const fetchReports = useCallback(() => {
    operationGetResults(operation.id)
      .then(({ data }) => {
        setReportList(data.result);
      })
      .catch(err => {
        setError(
          err?.response?.data?.message || formatMessage({ id: "erro-api" })
        );
      });
  }, [operation.id, setError, formatMessage]);

  const fetchCoordinates = useCallback(() => {
    exportCoordinatesSicor(operation.id)
      .then(({ data }) => {
        setCsvReport(data);
      })
      .catch(err => {
        setError(
          err?.response?.data?.message || formatMessage({ id: "erro-api" })
        );
      });
  }, [operation.id, setError, formatMessage]);

  const fetchCar = useCallback(() => {
    operationGetCarLayers(operation.id, SHOW_LAYERS)
      .then(({ data }) => {
        const carLayersResult = data.result;
        operationGetGlebas(operation.id).then(response => {
          const glebasResult = response.data.result;

          const apiCarList = carLayersResult.map(car => {
            const glebasList = glebasResult.filter(
              gleba => gleba.b3_car_id === car.B3Car.id
            );

            return {
              ...car,
              Gleba: glebasList
            };
          });

          setOperationGlebas(glebasResult);
          setOperationCars(apiCarList);
        });
      })
      .catch(err => {
        setError(
          err?.response?.data?.message || formatMessage({ id: "erro-api" })
        );
      });
  }, [operation.id, setError, formatMessage]);

  const handleExportCsvCar = () => {
    setIsLoading(true);
    const selectedCarId = results.B3Consulta.requisicao_json.area_id;
    const foundExport = operationCars.find(
      car => car.B3Car.B3Area[0].id === selectedCarId
    );

    if (!foundExport) return;

    const carIds = foundExport.B3Car.B3Area.map(
      car => car.B3AreasB3Car.b3_car_id
    );
    exportSicorCar(carIds).then(res => {
      const csvExport = res.data;
      setCsvExport(csvExport);
      csvref.current.link.click();
      setIsLoading(false);
    });
  };

  const resultsCriterio = (type, buscar) => {
    setResults(null);
    setResultLoading(true);
    setSelectedReport(buscar);

    getResults(buscar)
      .then(res => {
        const results = { ...res.data };
        const documentCriteria = {};

        res.data.B3ConsultaCriterioPessoa.forEach(crit => {
          if (!documentCriteria.hasOwnProperty(crit.B3Criterio.id)) {
            documentCriteria[crit.B3Criterio.id] = {
              B3Criterio: crit.B3Criterio,
              B3CriterioVersao: crit.B3CriterioVersao,
              data_consulta:
                crit.detalhes_json && crit.detalhes_json.data_consulta,
              documents: {
                has_infractions: [],
                clean: []
              }
            };
          } else if (
            !documentCriteria[crit.B3Criterio.id].data_consulta &&
            crit.detalhes_json
          ) {
            documentCriteria[crit.B3Criterio.id].data_consulta =
              crit.detalhes_json.data_consulta;
          }

          documentCriteria[crit.B3Criterio.id].documents[
            crit.conforme ? "clean" : "has_infractions"
          ].push(crit);
        });

        results.documentCriteria = Object.values(documentCriteria);

        if (type === "gleba") {
          getResultsMapLayers(buscar).then(res => {
            setGlebaLayers(res.data.result);
          });
        }

        setResultType(type);
        setResults(results);
        setResultLoading(false);
      })
      .catch(err => {
        setError(
          err?.response?.data?.message || formatMessage({ id: "erro-api" })
        );
      });
  };

  useEffect(() => {
    fetchCoordinates();
    fetchReports();
    fetchCar();
  }, [fetchReports, fetchCar, fetchCoordinates]);

  const generateReportPdf = useCallback(() => {
    if (!results) {
      return;
    }

    if (
      operationStartFrom === "car" &&
      results.B3ConsultaCriterioArea.length > 0
    ) {
      const selectedAreaId = results.B3ConsultaCriterioArea[0].b3_area_id;

      const foundCar = operationCars.find(
        car => car.B3Car.B3Area[0].id === selectedAreaId
      );

      if (!foundCar) return;
    }

    addLoadingItem("pdf");

    generateResultPdf(results.B3Consulta.id, selectedCriteria, "selected")
      .then(response => {
        window.open(response.data.url_pdf, "_blank");
      })
      .catch(err => {
        setError(
          err?.response?.data?.message || formatMessage({ id: "erro-api" })
        );
      })
      .finally(() => {
        removeLoadingItem("pdf");
      });
  }, [
    results,
    operationCars,
    formatMessage,
    setError,
    addLoadingItem,
    removeLoadingItem,
    operationStartFrom,
    selectedCriteria
  ]);

  const generateOperationPdf = useCallback(() => {
    addLoadingItem("pdf");
    generateResultPdfOperacao(operation.id)
      .then(response => {
        window.open(response.data.url_pdf, "_blank");
      })
      .catch(err => {
        setError(
          err?.response?.data?.message || formatMessage({ id: "erro-api" })
        );
      })
      .finally(() => {
        removeLoadingItem("pdf");
      });
  }, [
    operation.id,
    formatMessage,
    setError,
    addLoadingItem,
    removeLoadingItem
  ]);

  const downloadFile = (filename, data) => {
    let link = document.createElement("a");

    const blob = new Blob([data]);
    const fileDownloadUrl = URL.createObjectURL(blob);

    link.setAttribute("href", fileDownloadUrl);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportJsonClick = () => {
    addLoadingItem("json");
    exportOperacaoJson(operation.id).then(res => {
      const data = res.data.result;
      downloadFile(`${data.operacao.nome}.json`, JSON.stringify(data, null, 2));
      removeLoadingItem("json");
    });
  };

  const handleExportOperationKmlClick = () => {
    addLoadingItem("kml");
    exportGlebaPropostaKml(operation.id).then(res => {
      downloadFile(`${operation.id}.kml`, res.data);
      removeLoadingItem("kml");
    });
  };

  const handleExportReportKmlClick = () => {
    const selectedAreaId = results.B3ConsultaCriterioArea[0].b3_area_id;
    const foundCar = operationCars.find(
      car => car.B3Car.B3Area[0].id === selectedAreaId
    );

    if (!foundCar) return;

    addLoadingItem("kml");
    const glebaIds = foundCar.Gleba.map(gleba => gleba.id);
    exportGlebaConsultaKml(glebaIds).then(res => {
      downloadFile(`glebas_${selectedAreaId}.kml`, res.data);
      removeLoadingItem("kml");
    });
  };

  const toggleCollapse = useCallback(id => {
    setOpenCriteria(openCriteria =>
      openCriteria.includes(id)
        ? openCriteria.filter(crit => crit !== id)
        : [...openCriteria, id]
    );
  }, []);

  const selectedCar = useMemo(() => {
    if (reportList?.length === 0 || operationCars?.length === 0) return;

    const reportPicked = reportList.find((report) => {
      return report?.id === selectedReport
    });

    if (!reportPicked) return;

    const reportAreaId = reportPicked?.requisicao_json?.area_id;
    if (!reportAreaId) return;

    const car = operationCars.find((car) => {
      return car.B3Car.B3Area?.[0].id === reportAreaId
    });

    return car;
  }, [operationCars, selectedReport, reportList]);

  const isGeralConforme = (reportList ?? [])
    .map(result => result.conformidade)
    .every(Boolean);

  return (
    <>
      <h3 className="">{stepLabel}</h3>
      <div className="">
        <p>{formatMessage({ id: "descricao-resultados" })}</p>
      </div>

      <div className="geral">
        <div className="tile is-ancestor">
          <div className="tile is-5 is-vertical">
            <div className="is-12">
              <h4>{formatMessage({ id: "geral" })}</h4>
              <span>{formatMessage({ id: "descricao-geral-resultados" })}</span>
              <div
                className={`box-consulta 
                    ${isGeralConforme ? "box-conforme" : "box-desconforme"}
                    ${selectedReport === "geral" ? "box-selected" : ""}
                  `}
                onClick={() => {
                  setResults(null);
                  setResultType("geral");
                  setSelectedReport("geral");
                }}
              >
                <span className="box-nome">
                  {formatMessage({ id: "geral" })}
                </span>
                {isGeralConforme ? (
                  <MdCheckCircle size={20} />
                ) : (
                  <MdCancel size={20} />
                )}
              </div>
            </div>
            <div>
              <div className="geral-info is-12">
                <h4>{formatMessage({ id: "Documento" }, { num: 2 })}</h4>
                <span>
                  {formatMessage({ id: "descricao-documento-resultados" })}
                </span>
                {reportList.map(
                  buscar =>
                    buscar.tipo === "pessoa" && (
                      <div
                        className={`box-consulta 
                          ${buscar.conformidade
                            ? "box-conforme"
                            : "box-desconforme"
                          }
                          ${buscar.id === selectedReport ? "box-selected" : ""}
                        `}
                        onClick={() => resultsCriterio("cpf_cnpj", buscar.id)}
                        key={buscar.requisicao_json.cpf_cnpj}
                      >
                        <div className="box-inside">
                          <div className="box-cpf">
                            {buscar.requisicao_json.cpf_cnpj &&
                              buscar.requisicao_json.nome}
                          </div>
                        </div>
                        {buscar.conformidade ? (
                          <MdCheckCircle size={20} />
                        ) : (
                          <MdCancel size={20} />
                        )}
                      </div>
                    )
                )}
              </div>
              <div className="geral-info is-12">
                <h4>CAR</h4>
                <span>{formatMessage({ id: "descricao-car-resultados" })}</span>
                {reportList.map(
                  buscar =>
                    buscar.tipo === "car" && (
                      <div
                        className={`box-consulta 
                          ${buscar.conformidade
                            ? "box-conforme"
                            : "box-desconforme"
                          }
                          ${buscar.id === selectedReport ? "box-selected" : ""}
                        `}
                        key={buscar.requisicao_json.area_id}
                        onClick={() => resultsCriterio("car", buscar.id)}
                      >
                        <div className="column-car">
                          <div className="row-car">
                            <div className="proposta-car">
                              {buscar.requisicao_json.area_id &&
                                buscar.requisicao_json.nome}
                            </div>
                          </div>
                        </div>
                        {buscar.conformidade ? (
                          <MdCheckCircle size={20} />
                        ) : (
                          <MdCancel size={20} />
                        )}
                      </div>
                    )
                )}
              </div>
              {operationStartFrom === "gleba" && (
                <div className="geral-info is-12">
                  <h4>{formatMessage({ id: "glebas" })}</h4>
                  <span>
                    {formatMessage({ id: "descricao-gleba-resultados" })}
                  </span>
                  {reportList.map(
                    buscar =>
                      buscar.tipo === "gleba" && (
                        <div
                          className={`box-consulta 
                          ${buscar.conformidade
                              ? "box-conforme"
                              : "box-desconforme"
                            }
                          ${buscar.id === selectedReport ? "box-selected" : ""}
                        `}
                          key={buscar.requisicao_json.area_id}
                          onClick={() => resultsCriterio("gleba", buscar.id)}
                        >
                          <div className="column-car">
                            <div className="row-car">
                              <div className="proposta-car">
                                {buscar.requisicao_json.area_id &&
                                  buscar.requisicao_json.nome}
                              </div>
                            </div>
                          </div>
                          {buscar.conformidade ? (
                            <MdCheckCircle size={20} />
                          ) : (
                            <MdCancel size={20} />
                          )}
                        </div>
                      )
                  )}
                </div>
              )}
            </div>
            <div className="geral info is-15">
              <CriteriaList
                loading={!!results}
                areaCriteria={results?.B3ConsultaCriterioArea}
                documentCriteria={results?.documentCriteria}
                selectedCriteria={selectedCriteria}
                setSelectedCriteria={setSelectedCriteria}
                selectedTemplate={selectedTemplate}
                setSelectedTemplate={setSelectedTemplate}
              />
            </div>
          </div>

          <div className="tile is-vertical is-7">
            <div className="geral-resultado">
              <div className="area-info">
                {results && (
                  <>
                    {operationCars.map((car, carIndex) => {
                      if (
                        car.B3Car.B3Area[0].id !==
                        results.B3Consulta.requisicao_json.area_id
                      ) {
                        return null;
                      }

                      return (
                        <CarMap
                          areaInfo={car.B3Car.B3Area}
                          layers={car.CarLayer}
                          className="area-map"
                          key={carIndex}
                        />
                      );
                    })}

                      {resultType === "gleba" &&
                        operationGlebas.map((gleba, glebaIndex) => {
                          if (
                            gleba.B3AreasB3PropostaGleba[0].B3Area.id !==
                            results.B3Consulta.requisicao_json.area_id
                          ) {
                            return null;
                          }

                          return (
                            <OverlapPlots
                              key={glebaIndex}
                              gleba={gleba}
                              results={results}
                              glebaLayers={glebaLayers}
                            />
                          );
                        })}
                  </>
                )}
              </div>
              {resultLoading && <div className="loader"></div>}
              {!resultLoading && (
                <div className="resultados-info">
                  <h4>
                    {resultType === "geral" &&
                      `${formatMessage({ id: "resultados" })} ${formatMessage({
                        id: "geral"
                      })}`}
                    {resultType === "cpf_cnpj" &&
                      `${formatMessage({ id: "resultados" })} ${formatMessage(
                        {
                          id: "Documento"
                        },
                        { num: 2 }
                      )}`}
                    {resultType === "car" &&
                      `${formatMessage({ id: "resultados" })} CAR`}

                    {resultType === "gleba" &&
                      `${formatMessage({ id: "resultados" })} ${formatMessage({
                        id: "gleba"
                      })}`}
                  </h4>
                  <div className="results-buttons">
                    {["geral", "car"].includes(resultType) && (
                      <div>
                        <>
                          <span>{formatMessage({ id: "exportar-kml" })}</span>
                          <ExportButton
                            textId="exportar-kml"
                            isLoading={loadingItems.includes("kml")}
                            onClick={() => {
                              if (resultType === "geral")
                                handleExportOperationKmlClick();
                              else if (resultType === "car")
                                handleExportReportKmlClick();
                            }}
                          />
                        </>
                      </div>
                    )}
                    <div>
                      <span>
                        {formatMessage({ id: "exportar-resultados" })}
                      </span>
                      <ExportButton
                        textId="exportar-como-pdf"
                        isLoading={loadingItems.includes("pdf")}
                        className={`button secondary export-pdf is-small${loadingItems.includes("pdf") ? " is-loading" : ""
                          }`}
                        onClick={() => {
                          if (resultType === "geral") generateOperationPdf();
                          else generateReportPdf();
                        }}
                      />
                    </div>
                    {resultType === "geral" && (
                      <div>
                        <span>{formatMessage({ id: "exportar-json" })}</span>
                        <ExportButton
                          textId="exportar-json"
                          onClick={handleExportJsonClick}
                          isLoading={loadingItems.includes("json")}
                        />
                      </div>
                    )}
                    {resultType === "geral" && (
                      <div>
                        <span>
                          {formatMessage({ id: "exportar-coordenadas" })}
                        </span>
                        <CSVLink
                          data={csvReport}
                          filename={"coordenadas-geral.csv"}
                          target="_blank"
                        >
                          <button className="button">
                            <MdDownload size={20} />
                            {formatMessage({ id: "exportar-coordenadas" })}
                          </button>
                        </CSVLink>
                      </div>
                    )}
                    {resultType === "car" && (
                      <div>
                        <span>
                          {formatMessage({ id: "exportar-coordenadas" })}
                        </span>
                        <button
                          className={`button ${isLoading ? " is-loading" : ""}`}
                          onClick={handleExportCsvCar}
                        >
                          <MdDownload size={20} />
                          {formatMessage({ id: "exportar-coordenadas" })}
                        </button>
                        <CSVLink
                          data={csvExport}
                          filename={"coordenadas-car.csv"}
                          target="_blank"
                          ref={csvref}
                          className="hidden"
                        ></CSVLink>
                      </div>
                    )}
                  </div>
                </div>
              )}
              {results && (
                <>
                  <div className="criterio-info">
                    <div className="car-info">
                      <p>
                        <span className="bold">
                          {formatMessage({ id: "data-consulta" })}:
                        </span>{" "}
                        {results ? (
                          <FormattedDate
                            value={new Date(results.B3Consulta.data_consulta)}
                          />
                        ) : (
                          <Skeleton width="100" />
                        )}
                      </p>
                      {results.B3ConsultaCriterioArea[0] && (
                        <>
                          {results.B3ConsultaCriterioArea[0].B3Area
                            .Municipio && (
                            <p>
                              <span className="bold">
                                {formatMessage({ id: "municipio" })}:
                              </span>{" "}
                              {
                                results.B3ConsultaCriterioArea[0].B3Area
                                  .Municipio.nome_municipio
                              }
                            </p>
                          )}
                          {results.B3ConsultaCriterioArea[0].B3Area &&
                            results.B3ConsultaCriterioArea[0].B3Area
                              .area_ha && (
                              <p>
                                <span className="bold">
                                  {formatMessage({ id: "area" })}:
                                </span>
                                {` ${formatNumber(
                                  results.B3ConsultaCriterioArea[0].B3Area
                                    .area_ha
                                )} ha`}
                              </p>
                            )}
                        </>
                      )}
                    </div>
                    <div className="tile-criterio">
                      <div className="criterioslist">
                        {results &&
                          results.B3ConsultaCriterioArea.length > 0 && (
                            <AreaCriteria
                              criteria={results.B3ConsultaCriterioArea}
                              selectedCriteria={selectedCriteria}
                              openCriteria={openCriteria}
                              toggleCollapse={toggleCollapse}
                            />
                          )}

                        {results &&
                          results.B3ConsultaCriterioPessoa.length > 0 && (
                            <DocumentCriteria
                              criteria={results.documentCriteria}
                              selectedCriteria={selectedCriteria}
                              openCriteria={openCriteria}
                              toggleCollapse={toggleCollapse}
                            />
                          )}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default OperationResults;
